import { normalize } from 'normalizr'

import { AccountingSystemModel } from "@models/accountingSystem"

import api from "@functions/api"

import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import Schemas from '@redux/schema'
import { AppDispatch } from "@redux/store"
import { updateEntities } from '@redux/entities'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/accountingSystems/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/accountingSystems/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/accountingSystems/FETCH_FAILURE'

export type AccountingSystemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AccountingSystemModuleState = {
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAccountingSystems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().accountingSystems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/accounting-systems', options)
      .then(({ data }: { data: AccountingSystemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ACCOUNTINGSYSTEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())
        
        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AccountingSystemModuleState = initialState,
  action: Action = { type: '' },
): AccountingSystemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
